import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  adminAdjustCredit,
  adminAdjustPublicUsageUsed,
  adminGetOverride,
  adminGetPublicUsage,
  adminGetCreditEvents,
  adminGetUserByEmail,
  adminGetUserByUserId,
  adminGetUserByWhatsapp,
  adminRefreshPublicUsageFromWoo,
  adminSetOverride,
  adminSetPublicUsageStatus,
  adminUpdateReminderFlags,
  adminUpdateUserStatus,
  adminUpsertPublicUsageCap,
} from "./adminApi";
import AdminCreditEventsTable, { formatAdminCreditDate } from "./AdminCreditEventsTable";
import "./AdminDashboard.css";

const CHATBOT_NUMBER_OPTIONS = [
  { label: "Production (+6588707166)", value: "+6588707166" },
  { label: "Admin (+6598227828)", value: "+6598227828" },
  { label: "Sandbox (+16079009037)", value: "+16079009037" },
];

const DEFAULT_CHATBOT_NUMBER = "+6588707166";

function getUserPayload(response) {
  return (
    response?.data?.user ||
    response?.user ||
    response?.data ||
    response?.lookup?.user ||
    response?.lookup ||
    response ||
    {}
  );
}

function getOverridePayload(response) {
  return response?.data || response?.override || response || {};
}

function getPublicUsagePayload(response) {
  return response?.data || response || {};
}

function getPublicUsageRows(publicUsageData) {
  const rows =
    publicUsageData?.subscription_rows ||
    publicUsageData?.rows ||
    publicUsageData?.public_usage_rows ||
    [];
  return Array.isArray(rows) ? rows : [];
}

function getPublicUsageSummary(publicUsageData) {
  return (
    publicUsageData?.summary ||
    publicUsageData?.public_usage_summary ||
    publicUsageData?.public_subscription_summary ||
    {}
  );
}

function getSubscriptionId(row) {
  return row?.subscription_id || row?.RowKey || row?.row_key || "";
}

function getPublicSummaryHasActive(summary) {
  return summary?.has_active_public_subscription_usage ?? summary?.has_active_public_subscription;
}

function getPublicSummarySubscriptionId(summary) {
  return summary?.active_subscription_id || getSubscriptionId(summary);
}

function formatAdminValue(value) {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "boolean") return String(value);
  return value;
}

function getCanonicalUserId(user) {
  return user?.user_id || user?.id || user?.public_user_id || "";
}

function getCanonicalEmail(user) {
  return user?.user_email || user?.email || "";
}

function getCanonicalWhatsappNumber(user) {
  return (
    user?.user_whatsapp_number ||
    user?.whatsapp_number ||
    user?.sender_number ||
    user?.last_twilio_whatsapp_number_accessed ||
    ""
  );
}

function getSyncPathItems(user) {
  return [
    ["saved_path", user?.saved_path],
    ["synced_path", user?.synced_path],
    ["saved_user_path", user?.saved_user_path],
    ["synced_user_path", user?.synced_user_path],
    ["table_path", user?.table_path],
    ["blob_path", user?.blob_path],
  ].filter(([, value]) => value);
}

export default function AdminUserLookupPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [whatsappNumber, setWhatsappNumber] = useState("");
  const [clientWhatsappNumber, setClientWhatsappNumber] = useState(DEFAULT_CHATBOT_NUMBER);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [emailLookup, setEmailLookup] = useState("");
  const [emailLookupUserType, setEmailLookupUserType] = useState("public_user");
  const [userData, setUserData] = useState(null);
  const [currentUserId, setCurrentUserId] = useState("");
  const [currentEmail, setCurrentEmail] = useState("");
  const [currentWhatsappNumber, setCurrentWhatsappNumber] = useState("");
  const [currentClientWhatsappNumber, setCurrentClientWhatsappNumber] = useState(DEFAULT_CHATBOT_NUMBER);
  const [creditBucket, setCreditBucket] = useState("paid");
  const [creditDelta, setCreditDelta] = useState("");
  const [overrideNote, setOverrideNote] = useState("");
  const [selectedUserStatus, setSelectedUserStatus] = useState("active");
  const [reminderScienceGraderInitialFlag, setReminderScienceGraderInitialFlag] = useState("off");
  const [reminderEngCompoGraderInitialFlag, setReminderEngCompoGraderInitialFlag] = useState("off");
  const [reminderScienceGraderFlag, setReminderScienceGraderFlag] = useState("off");
  const [reminderGenericFlag, setReminderGenericFlag] = useState("off");
  const [publicUsageData, setPublicUsageData] = useState(null);
  const [publicUsageLoading, setPublicUsageLoading] = useState(false);
  const [publicUsageActionLoading, setPublicUsageActionLoading] = useState(false);
  const [publicUsageLookupSubscriptionId, setPublicUsageLookupSubscriptionId] = useState("");
  const [publicUsageAdjustSubscriptionId, setPublicUsageAdjustSubscriptionId] = useState("");
  const [publicUsageAdjustDelta, setPublicUsageAdjustDelta] = useState("");
  const [publicUsageCapSubscriptionId, setPublicUsageCapSubscriptionId] = useState("");
  const [publicUsageCapValue, setPublicUsageCapValue] = useState("");
  const [publicUsageStatusSubscriptionId, setPublicUsageStatusSubscriptionId] = useState("");
  const [publicUsageStatusValue, setPublicUsageStatusValue] = useState("active");
  const [publicUsageRefreshSubscriptionId, setPublicUsageRefreshSubscriptionId] = useState("");
  const [creditEvents, setCreditEvents] = useState([]);
  const [creditEventsLoading, setCreditEventsLoading] = useState(false);
  const [creditEventsError, setCreditEventsError] = useState("");
  const [creditHistoryFrom, setCreditHistoryFrom] = useState(null);

  function clearCreditEvents() {
    setCreditEvents([]);
    setCreditEventsError("");
    setCreditHistoryFrom(null);
  }

  async function loadCreditEventsForUser(userId) {
    if (!userId) { clearCreditEvents(); return; }
    setCreditEventsLoading(true); setCreditEventsError("");
    try {
      const data = await adminGetCreditEvents({ user_id: userId, limit: 50 });
      setCreditEvents(Array.isArray(data?.events) ? data.events : []);
      setCreditHistoryFrom(data?.history_complete_from || null);
    } catch (err) {
      setCreditEvents([]); setCreditHistoryFrom(null);
      if (err?.status === 401) navigate("/admin/login", { replace: true });
      else setCreditEventsError("Credit activity could not be loaded.");
    } finally { setCreditEventsLoading(false); }
  }

  function setLoadedUser(user, override = {}) {
    const userId = getCanonicalUserId(user);
    const email = getCanonicalEmail(user);
    const whatsapp = getCanonicalWhatsappNumber(user);

    setUserData({
      ...user,
      user_id: userId || user?.user_id,
      user_email: email || user?.user_email,
      user_whatsapp_number: whatsapp,
      override_active:
        override?.is_active ??
        override?.override_active ??
        user?.override_active ??
        false,
      override_status:
        override?.status ||
        override?.override_status ||
        user?.override_status ||
        "",
    });
    setSelectedUserStatus(user?.user_status || "active");
    setReminderScienceGraderInitialFlag(
      user?.reminder_science_grader_initial_flag || "off"
    );
    setReminderEngCompoGraderInitialFlag(
      user?.reminder_eng_compo_grader_initial_flag || "off"
    );
    setReminderScienceGraderFlag(user?.reminder_science_grader_flag || "off");
    setReminderGenericFlag(user?.reminder_generic_flag || "off");
    setCurrentUserId(userId);
    setCurrentEmail(email);
    setCurrentWhatsappNumber(whatsapp);
    setPublicUsageData(null);
  }

  async function loadUserByIdentity({
    user_id,
    email,
    whatsapp_number,
    client_whatsapp_number = currentClientWhatsappNumber,
    user_type = emailLookupUserType,
  } = {}) {
    const targetUserId = String(user_id || "").trim();
    const targetEmail = String(email || "").trim();
    const targetWhatsapp = String(whatsapp_number || "").trim();
    const targetClientWhatsapp = String(
      client_whatsapp_number || DEFAULT_CHATBOT_NUMBER
    ).trim();

    if (!targetUserId && !targetEmail && !targetWhatsapp) {
      setError("Please enter a user ID, email, or WhatsApp number.");
      return false;
    }

    setLookupLoading(true);
    setError("");
    setSuccess("");
    clearCreditEvents();

    try {
      let userResponse = null;
      if (targetUserId) {
        userResponse = await adminGetUserByUserId(targetUserId);
      } else if (targetEmail) {
        userResponse = await adminGetUserByEmail(targetEmail, user_type);
      } else {
        userResponse = await adminGetUserByWhatsapp(
          targetWhatsapp,
          targetClientWhatsapp
        );
      }

      let user = getUserPayload(userResponse);
      const resolvedUserId = getCanonicalUserId(user) || targetUserId;
      const resolvedEmail = getCanonicalEmail(user) || targetEmail;
      const resolvedWhatsapp = getCanonicalWhatsappNumber(user) || targetWhatsapp;
      let override = {};

      if (!targetUserId && resolvedUserId && !user?.user_credit && !user?.user_status) {
        try {
          const fullUserResponse = await adminGetUserByUserId(resolvedUserId);
          user = {
            ...user,
            ...getUserPayload(fullUserResponse),
          };
        } catch (_) {
          user = {
            ...user,
            user_id: resolvedUserId,
            user_email: resolvedEmail,
            user_whatsapp_number: resolvedWhatsapp,
          };
        }
      }

      if (resolvedWhatsapp) {
        try {
          const overrideResponse = await adminGetOverride(resolvedWhatsapp);
          override = getOverridePayload(overrideResponse);
        } catch (overrideErr) {
          if (overrideErr?.status === 401) {
            throw overrideErr;
          }
          override = {};
        }
      }

      setLoadedUser(
        {
          ...user,
          user_id: getCanonicalUserId(user) || resolvedUserId,
          user_email: getCanonicalEmail(user) || resolvedEmail,
          user_whatsapp_number: getCanonicalWhatsappNumber(user) || resolvedWhatsapp,
        },
        override
      );
      if (resolvedWhatsapp) {
        setWhatsappNumber(resolvedWhatsapp);
      }
      setCurrentClientWhatsappNumber(targetClientWhatsapp);
      await loadCreditEventsForUser(getCanonicalUserId(user) || resolvedUserId);
      return true;
    } catch (err) {
      if (err?.status === 401) {
        navigate("/admin/login", { replace: true });
        return false;
      }
      setError(err.message || "Failed to load user.");
      setUserData(null);
      setPublicUsageData(null);
      setCurrentUserId("");
      setCurrentEmail("");
      setCurrentWhatsappNumber("");
      clearCreditEvents();
      return false;
    } finally {
      setLookupLoading(false);
    }
  }

  useEffect(() => {
    const requestedUserId = searchParams.get("user_id");
    if (requestedUserId) loadUserByIdentity({ user_id: requestedUserId });
    // Load a linked canonical user once on page entry.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleLookup(event) {
    event.preventDefault();
    if (!whatsappNumber.trim()) {
      setError("Please enter a WhatsApp number.");
      return;
    }
    await loadUserByIdentity({
      whatsapp_number: whatsappNumber,
      client_whatsapp_number: clientWhatsappNumber,
    });
  }

  async function handleLookupByEmail(event) {
    event.preventDefault();

    if (!emailLookup.trim()) {
      setError("Please enter an email address.");
      return;
    }

    const loaded = await loadUserByIdentity({
      email: emailLookup.trim(),
      user_type: emailLookupUserType,
    });
    if (loaded) {
      setSuccess("Email account loaded.");
    }
  }

  async function handleAdjustCredit() {
    const parsedDelta = parseInt(creditDelta, 10);

    if (!currentUserId) {
      setError("Load a user first.");
      return;
    }

    if (Number.isNaN(parsedDelta)) {
      setError("Credit delta must be a valid integer.");
      return;
    }

    setActionLoading(true);
    setError("");
    setSuccess("");

    try {
      await adminAdjustCredit({
        user_id: currentUserId,
        email: currentEmail,
        whatsapp_number: currentWhatsappNumber,
        delta: parsedDelta,
        credit_bucket: creditBucket,
        client_whatsapp_number: currentClientWhatsappNumber,
      });
      const reloaded = await loadUserByIdentity({ user_id: currentUserId });
      if (reloaded) {
        setSuccess(
          creditBucket === "paid"
            ? "Paid credit updated."
            : "Promotional credit updated."
        );
      }
    } catch (err) {
      if (err?.status === 401) {
        navigate("/admin/login", { replace: true });
        return;
      }
      setError(err.message || "Failed to adjust credit.");
    } finally {
      setActionLoading(false);
    }
  }

  async function handleOverrideChange(isActive) {
    if (!currentWhatsappNumber) {
      setError("WhatsApp-only feature. This user has no WhatsApp number linked.");
      return;
    }

    setActionLoading(true);
    setError("");
    setSuccess("");

    try {
      await adminSetOverride(currentWhatsappNumber, isActive, overrideNote);
      setSuccess(isActive ? "Override enabled." : "Override disabled.");
      await loadUserByIdentity({ user_id: currentUserId });
    } catch (err) {
      if (err?.status === 401) {
        navigate("/admin/login", { replace: true });
        return;
      }
      setError(err.message || "Failed to update override.");
    } finally {
      setActionLoading(false);
    }
  }

  async function handleUpdateUserStatus() {
    if (!currentUserId) {
      setError("Load a user first.");
      return;
    }

    setActionLoading(true);
    setError("");
    setSuccess("");

    try {
      await adminUpdateUserStatus(
        {
          user_id: currentUserId,
          email: currentEmail,
          whatsapp_number: currentWhatsappNumber,
          client_whatsapp_number: currentClientWhatsappNumber,
        },
        selectedUserStatus,
        currentClientWhatsappNumber
      );
      setSuccess("User status updated.");
      await loadUserByIdentity({ user_id: currentUserId });
    } catch (err) {
      if (err?.status === 401) {
        navigate("/admin/login", { replace: true });
        return;
      }
      setError(err.message || "Failed to update user status.");
    } finally {
      setActionLoading(false);
    }
  }

  async function handleUpdateReminderFlags() {
    if (!currentWhatsappNumber) {
      setError("WhatsApp-only feature. This user has no WhatsApp number linked.");
      return;
    }

    setActionLoading(true);
    setError("");
    setSuccess("");

    try {
      await adminUpdateReminderFlags(
        currentWhatsappNumber,
        {
          reminder_science_grader_initial_flag:
            reminderScienceGraderInitialFlag,
          reminder_eng_compo_grader_initial_flag:
            reminderEngCompoGraderInitialFlag,
          reminder_science_grader_flag: reminderScienceGraderFlag,
          reminder_generic_flag: reminderGenericFlag,
        },
        currentClientWhatsappNumber
      );
      setSuccess("Reminder flags updated.");
      await loadUserByIdentity({ user_id: currentUserId });
    } catch (err) {
      if (err?.status === 401) {
        navigate("/admin/login", { replace: true });
        return;
      }
      setError(err.message || "Failed to update reminder flags.");
    } finally {
      setActionLoading(false);
    }
  }

  async function loadPublicUsage(options = {}) {
    const userId = currentUserId || userData?.user_id || "";
    const subscriptionId =
      options.subscription_id !== undefined
        ? options.subscription_id
        : publicUsageLookupSubscriptionId.trim();

    if (!userId && !subscriptionId) {
      setError("Load a user with user_id first or enter a subscription_id.");
      return;
    }

    setPublicUsageLoading(true);
    setError("");
    setSuccess("");

    try {
      const response = await adminGetPublicUsage({
        user_id: userId,
        email: currentEmail,
        subscription_id: subscriptionId,
      });
      const payload = getPublicUsagePayload(response);
      const activeUsage = payload.active_usage || {};
      const resolvedSubscriptionId =
        getSubscriptionId(activeUsage) ||
        getSubscriptionId(getPublicUsageRows(payload)[0]) ||
        subscriptionId;

      setPublicUsageData(payload);
      if (resolvedSubscriptionId) {
        if (!publicUsageAdjustSubscriptionId) {
          setPublicUsageAdjustSubscriptionId(resolvedSubscriptionId);
        }
        if (!publicUsageCapSubscriptionId) {
          setPublicUsageCapSubscriptionId(resolvedSubscriptionId);
        }
        if (!publicUsageStatusSubscriptionId) {
          setPublicUsageStatusSubscriptionId(resolvedSubscriptionId);
        }
        if (!publicUsageRefreshSubscriptionId) {
          setPublicUsageRefreshSubscriptionId(resolvedSubscriptionId);
        }
      }
    } catch (err) {
      if (err?.status === 401) {
        navigate("/admin/login", { replace: true });
        return;
      }
      setError(err.message || "Failed to load public subscription usage.");
      setPublicUsageData(null);
    } finally {
      setPublicUsageLoading(false);
    }
  }

  async function reloadPublicUsageAndUser(subscriptionId = "") {
    if (currentUserId) {
      await loadUserByIdentity({ user_id: currentUserId });
    }
    await loadPublicUsage({ subscription_id: subscriptionId || publicUsageLookupSubscriptionId.trim() });
  }

  async function handleLoadPublicUsage() {
    await loadPublicUsage();
  }

  async function handleAdjustPublicUsageUsed() {
    const userId = currentUserId || userData?.user_id || "";
    const subscriptionId = publicUsageAdjustSubscriptionId.trim();
    const delta = parseInt(publicUsageAdjustDelta, 10);

    if (!userId) {
      setError("Load a user with user_id first.");
      return;
    }
    if (!subscriptionId) {
      setError("subscription_id is required to adjust public subscription usage.");
      return;
    }
    if (Number.isNaN(delta)) {
      setError("Delta must be a valid integer.");
      return;
    }

    setPublicUsageActionLoading(true);
    setError("");
    setSuccess("");

    try {
      await adminAdjustPublicUsageUsed({
        user_id: userId,
        subscription_id: subscriptionId,
        delta,
      });
      await reloadPublicUsageAndUser(subscriptionId);
      setSuccess("Public subscription used credits adjusted.");
    } catch (err) {
      if (err?.status === 401) {
        navigate("/admin/login", { replace: true });
        return;
      }
      setError(err.message || "Failed to adjust public subscription usage.");
    } finally {
      setPublicUsageActionLoading(false);
    }
  }

  async function handleUpsertPublicUsageCap() {
    const userId = currentUserId || userData?.user_id || "";
    const subscriptionId = publicUsageCapSubscriptionId.trim();
    const subscriptionCreditCap = parseInt(publicUsageCapValue, 10);

    if (!userId) {
      setError("Load a user with user_id first.");
      return;
    }
    if (!subscriptionId) {
      setError("subscription_id is required to update the public subscription cap.");
      return;
    }
    if (Number.isNaN(subscriptionCreditCap)) {
      setError("Subscription credit cap must be a valid integer.");
      return;
    }

    setPublicUsageActionLoading(true);
    setError("");
    setSuccess("");

    try {
      await adminUpsertPublicUsageCap({
        user_id: userId,
        subscription_id: subscriptionId,
        subscription_credit_cap: subscriptionCreditCap,
      });
      await reloadPublicUsageAndUser(subscriptionId);
      setSuccess("Public subscription credit cap updated.");
    } catch (err) {
      if (err?.status === 401) {
        navigate("/admin/login", { replace: true });
        return;
      }
      setError(err.message || "Failed to update public subscription cap.");
    } finally {
      setPublicUsageActionLoading(false);
    }
  }

  async function handleSetPublicUsageStatus() {
    const userId = currentUserId || userData?.user_id || "";
    const subscriptionId = publicUsageStatusSubscriptionId.trim();

    if (!userId) {
      setError("Load a user with user_id first.");
      return;
    }
    if (!subscriptionId) {
      setError("subscription_id is required to set public subscription status.");
      return;
    }

    setPublicUsageActionLoading(true);
    setError("");
    setSuccess("");

    try {
      await adminSetPublicUsageStatus({
        user_id: userId,
        subscription_id: subscriptionId,
        status: publicUsageStatusValue,
      });
      await reloadPublicUsageAndUser(subscriptionId);
      setSuccess("Public subscription status updated.");
    } catch (err) {
      if (err?.status === 401) {
        navigate("/admin/login", { replace: true });
        return;
      }
      setError(err.message || "Failed to update public subscription status.");
    } finally {
      setPublicUsageActionLoading(false);
    }
  }

  async function handleRefreshPublicUsageFromWoo() {
    const userId = currentUserId || userData?.user_id || "";
    const subscriptionId = publicUsageRefreshSubscriptionId.trim();

    if (!userId) {
      setError("Load a user with user_id first.");
      return;
    }
    if (!subscriptionId) {
      setError("subscription_id is required to refresh public usage from Woo.");
      return;
    }

    setPublicUsageActionLoading(true);
    setError("");
    setSuccess("");

    try {
      await adminRefreshPublicUsageFromWoo({
        subscription_id: subscriptionId,
      });
      await reloadPublicUsageAndUser(subscriptionId);
      setSuccess("Public subscription usage refreshed from Woo.");
    } catch (err) {
      if (err?.status === 401) {
        navigate("/admin/login", { replace: true });
        return;
      }
      setError(err.message || "Failed to refresh public usage from Woo.");
    } finally {
      setPublicUsageActionLoading(false);
    }
  }

  const publicSubscriptionSummary = userData?.public_subscription_summary || null;
  const publicUsageRows = getPublicUsageRows(publicUsageData);
  const publicUsageSummary = getPublicUsageSummary(publicUsageData);
  const publicUsageActive = publicUsageData?.active_usage || null;
  const hasUserId = Boolean(currentUserId || userData?.user_id);
  const hasWhatsappNumber = Boolean(currentWhatsappNumber);
  const syncPathItems = getSyncPathItems(userData || {});

  return (
    <div className="admin-content">
      <div className="admin-card">
        <h2 className="admin-section-title">Email Lookup</h2>
        <form onSubmit={handleLookupByEmail}>
          <div className="admin-form-grid">
            <div className="admin-form-row">
              <label>Email</label>
              <input
                className="admin-input"
                value={emailLookup}
                onChange={(e) => setEmailLookup(e.target.value)}
                placeholder="user@example.com"
              />
            </div>

            <div className="admin-form-row">
              <label>User Type</label>
              <select
                className="admin-input"
                value={emailLookupUserType}
                onChange={(e) => setEmailLookupUserType(e.target.value)}
              >
                <option value="public_user">public_user</option>
                <option value="corporate_user">corporate_user</option>
              </select>
            </div>
          </div>

          <div className="admin-button-row">
            <button
              type="submit"
              className="admin-button admin-button-primary"
              disabled={lookupLoading}
            >
              {lookupLoading ? "Loading..." : "Load Account"}
            </button>
          </div>
        </form>
      </div>

      <div className="admin-card">
        <h2 className="admin-section-title">WhatsApp Lookup</h2>
        <form onSubmit={handleLookup}>
          <div className="admin-form-grid">
            <div className="admin-form-row">
              <label>WhatsApp Number</label>
              <input
                className="admin-input"
                value={whatsappNumber}
                onChange={(e) => setWhatsappNumber(e.target.value)}
                placeholder="+6598xxxxxx or whatsapp:+6598xxxxxx"
              />
            </div>

            <div className="admin-form-row">
              <label>Chatbot Number</label>
              <select
                className="admin-input"
                value={clientWhatsappNumber}
                onChange={(e) => setClientWhatsappNumber(e.target.value)}
              >
                {CHATBOT_NUMBER_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="admin-button-row">
            <button
              type="submit"
              className="admin-button admin-button-primary"
              disabled={lookupLoading}
            >
              {lookupLoading ? "Loading..." : "Load User"}
            </button>
          </div>
        </form>

        {error && <div className="admin-error" style={{ marginTop: 12 }}>{error}</div>}
        {success && <div className="admin-success" style={{ marginTop: 12 }}>{success}</div>}
      </div>

      <div className="admin-card">
        <h3 className="admin-section-title">User Summary</h3>
        {userData ? (
          <div className="admin-summary-grid">
            <div className="admin-summary-item"><b>User ID:</b> {currentUserId || userData.user_id || "-"}</div>
            <div className="admin-summary-item"><b>Email:</b> {currentEmail || userData.user_email || "-"}</div>
            <div className="admin-summary-item"><b>WhatsApp number:</b> {currentWhatsappNumber || userData.user_whatsapp_number || userData.whatsapp_number || "-"}</div>
            <div className="admin-summary-item"><b>user_firstname:</b> {userData.user_firstname || "-"}</div>
            <div className="admin-summary-item"><b>user_lastname:</b> {userData.user_lastname || "-"}</div>
            <div className="admin-summary-item"><b>Promotional credit:</b> {userData.promotional_credit_balance ?? "-"}</div>
            <div className="admin-summary-item"><b>Paid credit:</b> {userData.paid_credit_balance ?? "-"}</div>
            <div className="admin-summary-item"><b>Total credit:</b> {userData.user_credit ?? "-"}</div>
            <div className="admin-summary-item"><b>Credit schema version:</b> {userData.credit_schema_version ?? "-"}</div>
            <div className="admin-summary-item"><b>Status:</b> {userData.user_status || "-"}</div>
            <div className="admin-summary-item"><b>reminder_science_grader_initial_flag:</b> {userData.reminder_science_grader_initial_flag || "-"}</div>
            <div className="admin-summary-item"><b>reminder_eng_compo_grader_initial_flag:</b> {userData.reminder_eng_compo_grader_initial_flag || "-"}</div>
            <div className="admin-summary-item"><b>reminder_science_grader_flag:</b> {userData.reminder_science_grader_flag || "-"}</div>
            <div className="admin-summary-item"><b>reminder_generic_flag:</b> {userData.reminder_generic_flag || "-"}</div>
            <div className="admin-summary-item"><b>user_premium_status:</b> {userData.user_premium_status || "-"}</div>
            <div className="admin-summary-item"><b>user_subscription_id:</b> {userData.user_subscription_id || "-"}</div>
            <div className="admin-summary-item"><b>last_twilio_whatsapp_number_accessed:</b> {userData.last_twilio_whatsapp_number_accessed || "-"}</div>
            <div className="admin-summary-item"><b>override_active:</b> {String(userData.override_active ?? false)}</div>
            <div className="admin-summary-item"><b>override_status:</b> {userData.override_status || "-"}</div>
            <div className="admin-summary-item"><b>woo_entitled:</b> {String(userData.woo_entitled ?? false)}</div>
            <div className="admin-summary-item"><b>woo_reason:</b> {userData.woo_reason || "-"}</div>
            <div className="admin-summary-item"><b>woo_subscription_id_detected:</b> {userData.woo_subscription_id_detected || "-"}</div>
            {syncPathItems.map(([label, value]) => (
              <div className="admin-summary-item" key={label}>
                <b>{label}:</b> {formatAdminValue(value)}
              </div>
            ))}
            {publicSubscriptionSummary ? (
              <>
                <div className="admin-summary-item"><b>public has_active_public_subscription:</b> {formatAdminValue(getPublicSummaryHasActive(publicSubscriptionSummary))}</div>
                <div className="admin-summary-item"><b>public subscription_id:</b> {formatAdminValue(getPublicSummarySubscriptionId(publicSubscriptionSummary))}</div>
                <div className="admin-summary-item"><b>public subscription_status:</b> {formatAdminValue(publicSubscriptionSummary.subscription_status)}</div>
                <div className="admin-summary-item"><b>public plan_id:</b> {formatAdminValue(publicSubscriptionSummary.plan_id)}</div>
                <div className="admin-summary-item"><b>public plan_row_key:</b> {formatAdminValue(publicSubscriptionSummary.plan_row_key)}</div>
                <div className="admin-summary-item"><b>public subscription_credit_cap:</b> {formatAdminValue(publicSubscriptionSummary.subscription_credit_cap)}</div>
                <div className="admin-summary-item"><b>public subscription_credit_used:</b> {formatAdminValue(publicSubscriptionSummary.subscription_credit_used)}</div>
                <div className="admin-summary-item"><b>public subscription_credit_balance:</b> {formatAdminValue(publicSubscriptionSummary.subscription_credit_balance)}</div>
                <div className="admin-summary-item"><b>public period_end:</b> {formatAdminValue(publicSubscriptionSummary.period_end)}</div>
              </>
            ) : null}
          </div>
        ) : (
          <div className="admin-meta">No user loaded yet.</div>
        )}
      </div>

      {userData ? <div className="admin-card">
        <div className="admin-button-row" style={{ justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <h3 className="admin-section-title" style={{ margin: 0 }}>Credit Activity</h3>
          <div className="admin-button-row">
            <Link className="admin-button admin-button-secondary" to={`/admin/credit-activity?user_id=${encodeURIComponent(currentUserId)}`}>View All Activity for This User</Link>
            <button type="button" className="admin-button admin-button-secondary" onClick={() => loadCreditEventsForUser(currentUserId)} disabled={creditEventsLoading}>Refresh</button>
          </div>
        </div>
        {creditEventsLoading ? <div>Loading credit activity…</div> : creditEventsError ? <div><p className="admin-error">{creditEventsError}</p><button type="button" className="admin-button admin-button-secondary" onClick={() => loadCreditEventsForUser(currentUserId)}>Retry</button></div> : <>
          <AdminCreditEventsTable events={creditEvents} emptyMessage="No credit activity is available for this user." />
          {creditHistoryFrom && <p className="admin-meta admin-history-note">Credit activity is available from {formatAdminCreditDate(creditHistoryFrom, true)}.</p>}
        </>}
      </div> : null}

      {userData ? (
        <div className="admin-card">
          <h3 className="admin-section-title">Public Subscription Usage</h3>
          {!hasUserId ? (
            <div className="admin-error" style={{ marginBottom: 12 }}>
              Load a user with user_id first.
            </div>
          ) : null}

          <div className="admin-form-grid">
            <div className="admin-form-row">
              <label>Loaded user_id</label>
              <input
                className="admin-input"
                value={currentUserId || userData.user_id || ""}
                readOnly
                placeholder="No user_id loaded"
              />
            </div>
            <div className="admin-form-row">
              <label>Manual subscription_id lookup</label>
              <input
                className="admin-input"
                value={publicUsageLookupSubscriptionId}
                onChange={(e) => setPublicUsageLookupSubscriptionId(e.target.value)}
                placeholder="Optional subscription_id"
              />
            </div>
          </div>

          <div className="admin-button-row" style={{ marginBottom: 16 }}>
            <button
              type="button"
              className="admin-button admin-button-primary"
              onClick={handleLoadPublicUsage}
              disabled={publicUsageLoading || (!hasUserId && !publicUsageLookupSubscriptionId.trim())}
            >
              {publicUsageLoading ? "Loading..." : "Load Public Usage"}
            </button>
          </div>

          {publicUsageData ? (
            <div style={{ display: "grid", gap: 16 }}>
              <div>
                <h4 className="admin-section-title">Summary</h4>
                <div className="admin-summary-grid">
                  <div className="admin-summary-item"><b>status:</b> {formatAdminValue(publicUsageData.status)}</div>
                  <div className="admin-summary-item"><b>user_id:</b> {formatAdminValue(publicUsageData.user_id)}</div>
                  <div className="admin-summary-item"><b>has_active_public_subscription:</b> {formatAdminValue(getPublicSummaryHasActive(publicUsageSummary))}</div>
                  <div className="admin-summary-item"><b>subscription_id:</b> {formatAdminValue(getPublicSummarySubscriptionId(publicUsageSummary))}</div>
                  <div className="admin-summary-item"><b>subscription_status:</b> {formatAdminValue(publicUsageSummary.subscription_status)}</div>
                  <div className="admin-summary-item"><b>subscription_credit_cap:</b> {formatAdminValue(publicUsageSummary.subscription_credit_cap)}</div>
                  <div className="admin-summary-item"><b>subscription_credit_used:</b> {formatAdminValue(publicUsageSummary.subscription_credit_used)}</div>
                  <div className="admin-summary-item"><b>subscription_credit_balance:</b> {formatAdminValue(publicUsageSummary.subscription_credit_balance)}</div>
                </div>
              </div>

              <div>
                <h4 className="admin-section-title">Active Usage</h4>
                {publicUsageActive ? (
                  <div className="admin-summary-grid">
                    <div className="admin-summary-item"><b>subscription_id / RowKey:</b> {formatAdminValue(getSubscriptionId(publicUsageActive))}</div>
                    <div className="admin-summary-item"><b>plan_row_key:</b> {formatAdminValue(publicUsageActive.plan_row_key)}</div>
                    <div className="admin-summary-item"><b>plan_id:</b> {formatAdminValue(publicUsageActive.plan_id)}</div>
                    <div className="admin-summary-item"><b>status:</b> {formatAdminValue(publicUsageActive.subscription_status || publicUsageActive.status)}</div>
                    <div className="admin-summary-item"><b>subscription_credit_cap:</b> {formatAdminValue(publicUsageActive.subscription_credit_cap)}</div>
                    <div className="admin-summary-item"><b>subscription_credit_used:</b> {formatAdminValue(publicUsageActive.subscription_credit_used)}</div>
                    <div className="admin-summary-item"><b>subscription_credit_balance:</b> {formatAdminValue(publicUsageActive.subscription_credit_balance)}</div>
                    <div className="admin-summary-item"><b>period_start:</b> {formatAdminValue(publicUsageActive.period_start)}</div>
                    <div className="admin-summary-item"><b>period_end:</b> {formatAdminValue(publicUsageActive.period_end)}</div>
                    <div className="admin-summary-item"><b>updated_at:</b> {formatAdminValue(publicUsageActive.updated_at)}</div>
                    <div className="admin-summary-item"><b>last_webhook_event:</b> {formatAdminValue(publicUsageActive.last_webhook_event)}</div>
                    <div className="admin-summary-item"><b>last_admin_action:</b> {formatAdminValue(publicUsageActive.last_admin_action)}</div>
                  </div>
                ) : (
                  <div className="admin-meta">No active public subscription usage found.</div>
                )}
              </div>

              <div>
                <h4 className="admin-section-title">Subscription Rows</h4>
                {publicUsageRows.length ? (
                  <div style={{ display: "grid", gap: 12 }}>
                    {publicUsageRows.map((row, index) => (
                      <div
                        key={`${getSubscriptionId(row) || "public-usage"}-${index}`}
                        style={{
                          border: "1px solid #e5e7eb",
                          borderRadius: 10,
                          padding: 12,
                          background: "#f9fafb",
                        }}
                      >
                        <div className="admin-summary-grid">
                          <div className="admin-summary-item"><b>subscription_id / RowKey:</b> {formatAdminValue(getSubscriptionId(row))}</div>
                          <div className="admin-summary-item"><b>plan_row_key:</b> {formatAdminValue(row.plan_row_key)}</div>
                          <div className="admin-summary-item"><b>plan_id:</b> {formatAdminValue(row.plan_id)}</div>
                          <div className="admin-summary-item"><b>subscription_status / status:</b> {formatAdminValue(row.subscription_status || row.status)}</div>
                          <div className="admin-summary-item"><b>subscription_credit_cap:</b> {formatAdminValue(row.subscription_credit_cap)}</div>
                          <div className="admin-summary-item"><b>subscription_credit_used:</b> {formatAdminValue(row.subscription_credit_used)}</div>
                          <div className="admin-summary-item"><b>subscription_credit_balance:</b> {formatAdminValue(row.subscription_credit_balance)}</div>
                          <div className="admin-summary-item"><b>period_start:</b> {formatAdminValue(row.period_start)}</div>
                          <div className="admin-summary-item"><b>period_end:</b> {formatAdminValue(row.period_end)}</div>
                          <div className="admin-summary-item"><b>updated_at:</b> {formatAdminValue(row.updated_at)}</div>
                          <div className="admin-summary-item"><b>last_webhook_event:</b> {formatAdminValue(row.last_webhook_event)}</div>
                          <div className="admin-summary-item"><b>last_admin_action:</b> {formatAdminValue(row.last_admin_action)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="admin-meta">No public subscription usage found.</div>
                )}
              </div>
            </div>
          ) : (
            <div className="admin-meta">Load public usage to view subscription rows and balances.</div>
          )}

          <div style={{ borderTop: "1px solid #e5e7eb", marginTop: 18, paddingTop: 16 }}>
            <h4 className="admin-section-title">Adjust Used Credits</h4>
            <div className="admin-form-grid">
              <div className="admin-form-row">
                <label>subscription_id</label>
                <input
                  className="admin-input"
                  value={publicUsageAdjustSubscriptionId}
                  onChange={(e) => setPublicUsageAdjustSubscriptionId(e.target.value)}
                  placeholder="Required"
                />
              </div>
              <div className="admin-form-row">
                <label>delta</label>
                <input
                  className="admin-input"
                  value={publicUsageAdjustDelta}
                  onChange={(e) => setPublicUsageAdjustDelta(e.target.value)}
                  placeholder="e.g. -20 or 20"
                />
              </div>
            </div>
            <div className="admin-button-row">
              <button
                type="button"
                className="admin-button admin-button-primary"
                onClick={handleAdjustPublicUsageUsed}
                disabled={publicUsageActionLoading || !hasUserId}
              >
                Adjust Used Credits
              </button>
            </div>
          </div>

          <div style={{ borderTop: "1px solid #e5e7eb", marginTop: 18, paddingTop: 16 }}>
            <h4 className="admin-section-title">Update Credit Cap</h4>
            <div className="admin-form-grid">
              <div className="admin-form-row">
                <label>subscription_id</label>
                <input
                  className="admin-input"
                  value={publicUsageCapSubscriptionId}
                  onChange={(e) => setPublicUsageCapSubscriptionId(e.target.value)}
                  placeholder="Required"
                />
              </div>
              <div className="admin-form-row">
                <label>subscription_credit_cap</label>
                <input
                  className="admin-input"
                  value={publicUsageCapValue}
                  onChange={(e) => setPublicUsageCapValue(e.target.value)}
                  placeholder="e.g. 500"
                />
              </div>
            </div>
            <div className="admin-button-row">
              <button
                type="button"
                className="admin-button admin-button-primary"
                onClick={handleUpsertPublicUsageCap}
                disabled={publicUsageActionLoading || !hasUserId}
              >
                Update Credit Cap
              </button>
            </div>
          </div>

          <div style={{ borderTop: "1px solid #e5e7eb", marginTop: 18, paddingTop: 16 }}>
            <h4 className="admin-section-title">Set Status</h4>
            <div className="admin-form-grid">
              <div className="admin-form-row">
                <label>subscription_id</label>
                <input
                  className="admin-input"
                  value={publicUsageStatusSubscriptionId}
                  onChange={(e) => setPublicUsageStatusSubscriptionId(e.target.value)}
                  placeholder="Required"
                />
              </div>
              <div className="admin-form-row">
                <label>status</label>
                <select
                  className="admin-input"
                  value={publicUsageStatusValue}
                  onChange={(e) => setPublicUsageStatusValue(e.target.value)}
                >
                  <option value="active">active</option>
                  <option value="inactive">inactive</option>
                  <option value="cancelled">cancelled</option>
                  <option value="expired">expired</option>
                  <option value="payment_failed">payment_failed</option>
                </select>
              </div>
            </div>
            <div className="admin-button-row">
              <button
                type="button"
                className="admin-button admin-button-primary"
                onClick={handleSetPublicUsageStatus}
                disabled={publicUsageActionLoading || !hasUserId}
              >
                Set Public Usage Status
              </button>
            </div>
          </div>

          <div style={{ borderTop: "1px solid #e5e7eb", marginTop: 18, paddingTop: 16 }}>
            <h4 className="admin-section-title">Refresh From Woo</h4>
            <div className="admin-form-grid">
              <div className="admin-form-row">
                <label>subscription_id</label>
                <input
                  className="admin-input"
                  value={publicUsageRefreshSubscriptionId}
                  onChange={(e) => setPublicUsageRefreshSubscriptionId(e.target.value)}
                  placeholder="Required"
                />
              </div>
            </div>
            <div className="admin-button-row">
              <button
                type="button"
                className="admin-button admin-button-secondary"
                onClick={handleRefreshPublicUsageFromWoo}
                disabled={publicUsageActionLoading || !hasUserId}
              >
                Refresh From Woo
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="admin-card">
        <h3 className="admin-section-title">User Status Controls</h3>
        <div className="admin-form-grid">
          <div className="admin-form-row">
            <label>User Status</label>
            <select
              className="admin-input"
              value={selectedUserStatus}
              onChange={(e) => setSelectedUserStatus(e.target.value)}
            >
              <option value="active">active</option>
              <option value="inactive">inactive</option>
            </select>
          </div>
        </div>
        <div className="admin-button-row">
          <button
            type="button"
            className="admin-button admin-button-primary"
            onClick={handleUpdateUserStatus}
            disabled={actionLoading || !hasUserId}
          >
            Update Status
          </button>
        </div>
      </div>

      <div className="admin-card">
        <h3 className="admin-section-title">Reminder Flags</h3>
        {!hasWhatsappNumber ? (
          <div className="admin-meta" style={{ marginBottom: 12 }}>
            WhatsApp-only feature. This user has no WhatsApp number linked.
          </div>
        ) : null}
        <div className="admin-form-grid">
          <div className="admin-form-row">
            <label>reminder_science_grader_initial_flag</label>
            <select
              className="admin-input"
              value={reminderScienceGraderInitialFlag}
              onChange={(e) => setReminderScienceGraderInitialFlag(e.target.value)}
              disabled={!hasWhatsappNumber}
            >
              <option value="on">on</option>
              <option value="off">off</option>
            </select>
          </div>

          <div className="admin-form-row">
            <label>reminder_eng_compo_grader_initial_flag</label>
            <select
              className="admin-input"
              value={reminderEngCompoGraderInitialFlag}
              onChange={(e) => setReminderEngCompoGraderInitialFlag(e.target.value)}
              disabled={!hasWhatsappNumber}
            >
              <option value="on">on</option>
              <option value="off">off</option>
            </select>
          </div>

          <div className="admin-form-row">
            <label>reminder_science_grader_flag</label>
            <select
              className="admin-input"
              value={reminderScienceGraderFlag}
              onChange={(e) => setReminderScienceGraderFlag(e.target.value)}
              disabled={!hasWhatsappNumber}
            >
              <option value="on">on</option>
              <option value="off">off</option>
            </select>
          </div>

          <div className="admin-form-row">
            <label>reminder_generic_flag</label>
            <select
              className="admin-input"
              value={reminderGenericFlag}
              onChange={(e) => setReminderGenericFlag(e.target.value)}
              disabled={!hasWhatsappNumber}
            >
              <option value="on">on</option>
              <option value="off">off</option>
            </select>
          </div>
        </div>
        <div className="admin-button-row">
          <button
            type="button"
            className="admin-button admin-button-primary"
            onClick={handleUpdateReminderFlags}
            disabled={actionLoading || !hasWhatsappNumber}
          >
            Update Reminder Flags
          </button>
        </div>
      </div>

      <div className="admin-card">
        <h3 className="admin-section-title">Credit Controls</h3>
        <div className="admin-form-grid">
          <div className="admin-form-row">
            <label>Credit Bucket</label>
            <select
              className="admin-input"
              value={creditBucket}
              onChange={(e) => setCreditBucket(e.target.value)}
            >
              <option value="paid">Paid credit</option>
              <option value="promotional">Promotional credit</option>
            </select>
          </div>
          <div className="admin-form-row">
            <label>Credit Delta</label>
            <input
              className="admin-input"
              value={creditDelta}
              onChange={(e) => setCreditDelta(e.target.value)}
              placeholder="e.g. 5 or -3"
            />
          </div>
        </div>
        <div className="admin-button-row">
          <button
            type="button"
            className="admin-button admin-button-primary"
            onClick={handleAdjustCredit}
            disabled={actionLoading || !hasUserId}
          >
            Add / Adjust Credit
          </button>
        </div>
      </div>

      <div className="admin-card">
        <h3 className="admin-section-title">Override Controls</h3>
        {!hasWhatsappNumber ? (
          <div className="admin-meta" style={{ marginBottom: 12 }}>
            WhatsApp-only feature. This user has no WhatsApp number linked.
          </div>
        ) : null}
        <div className="admin-form-row">
          <label>Override Note</label>
          <textarea
            className="admin-textarea"
            value={overrideNote}
            onChange={(e) => setOverrideNote(e.target.value)}
            placeholder="Optional note for override changes"
            disabled={!hasWhatsappNumber}
          />
        </div>
        <div className="admin-button-row">
          <button
            type="button"
            className="admin-button admin-button-primary"
            onClick={() => handleOverrideChange(true)}
            disabled={actionLoading || !hasWhatsappNumber}
          >
            Enable Override
          </button>
          <button
            type="button"
            className="admin-button admin-button-danger"
            onClick={() => handleOverrideChange(false)}
            disabled={actionLoading || !hasWhatsappNumber}
          >
            Disable Override
          </button>
        </div>
      </div>

      <div className="admin-card">
        <h3 className="admin-section-title">Chat Shortcut</h3>
        {!hasWhatsappNumber ? (
          <div className="admin-meta" style={{ marginBottom: 12 }}>
            WhatsApp-only feature. This user has no WhatsApp number linked.
          </div>
        ) : null}
        <div className="admin-button-row">
          <button
            type="button"
            className="admin-button admin-button-secondary"
            disabled={!hasWhatsappNumber}
            onClick={() =>
              navigate(
                `/admin/chat?whatsapp_number=${encodeURIComponent(
                  currentWhatsappNumber
                )}&client_whatsapp_number=${encodeURIComponent(
                  currentClientWhatsappNumber
                )}`
              )
            }
          >
            Open Chat For This User
          </button>
        </div>
      </div>
    </div>
  );
}
